import './styles/ReviewCoverSheet.scss';
import PropTypes from 'prop-types';
import React from 'react';
import ReviewCoverSheetReview from './ReviewCoverSheetReview';
import ReviewInstructions from './ReviewInstructions';
import ReviewSpinner from './ReviewSpinner';
import history from 'shared_history';
import queryString from 'query-string';
import { ReviewContextType, ReviewType } from '../../../propTypes';
import { compose } from 'redux';
import { duckify } from 'utils/duckify';
import { schoolYears } from 'ducks';
import { withRouter } from 'react-router';
import ReviewCoverSheetFields from './ReviewCoverSheetFields';

/**
 * Main page for the contact review process.
 *
 * Displays information about the contact under review for a given year, and lets the user select a review year.
 *
 * An admin can select a review to display whereas a reader can only view assigned reviews.
 *
 * @param location {object} browser location object
 * @param params {object} react router path parameters
 * @param review {object} selected review (optional, may be null or undefined)
 * @param reviewContext {object} review contact metadata
 * @param reviewMeta {object} review prop duck metadata
 * @param schoolYearId {string} selected review year
 * @param schoolYears {array} all school years
 * @return {JSX.Element}
 */
const ReviewCoverSheet = ({
  location,
  params,
  review,
  reviewContext,
  reviewMeta,
  schoolYearId,
  schoolYears
}) => {
  const { contactId, reviewId } = params;
  const loading = !reviewContext.ready && (!reviewMeta || !reviewMeta.error);
  const error = reviewMeta && reviewMeta.error;

  const selectYear = (schoolYearId) => {
    const search = queryString.parse(location.search);
    history.push({
      ...location,
      pathname: `/contacts/${contactId}/reviews`,
      search: '?' + queryString.stringify({ ...search, schoolYearId })
    });
  };

  return (
    <div className="review-cover-sheet">
      {review && review.school_year_id && <ReviewInstructions
        user={{ first_name: 'Reader' }}
        reviewContext={reviewContext}
        schoolYearId={review.school_year_id}
      />}

      <div className="review-track-silo">
        View review for{' '}
        <select name="track_silo_id"
                value={schoolYearId}
                onChange={(e) => selectYear(e.target.value)}>
          {schoolYears
            .filter(sy => sy.review_enabled || sy.id === schoolYearId)
            .map(sy => <option key={sy.id} value={sy.id}>{sy.name}</option>)}
        </select>
      </div>

      <h1>Cover Sheet</h1>

      {loading && <ReviewSpinner/>}

      {error && <div className="alert alert-danger">
        {reviewMeta.errorMessage || 'Error loading review'}
      </div>}

      {!loading && !error && <React.Fragment>
        {!reviewId && <div className="alert alert-info">
          No reviews are available for this term. Assign a reader to this contact or select a different term.
        </div>}

        {reviewId && <React.Fragment>
          <ReviewCoverSheetFields reviewContext={reviewContext}/>

          <ReviewCoverSheetReview
            contactId={contactId}
            review={review}
            reviewMeta={reviewMeta}
            reviewContext={reviewContext}
          />
        </React.Fragment>}
      </React.Fragment>}
    </div>
  );
};

ReviewCoverSheet.propTypes = {
  /** Path parameters from react router */
  params: PropTypes.shape({
    contactId: PropTypes.string.isRequired,
    reviewId: PropTypes.string,
  }),

  /** Selected review */
  review: ReviewType,

  /** Review contact metadata */
  reviewContext: ReviewContextType.isRequired,
};

const mapDucksToProps = () => ({
  schoolYears,
});

export default compose(
  withRouter,
  duckify(mapDucksToProps)
)(ReviewCoverSheet);
